import mapValues from 'lodash-es/mapValues'
import omit from 'lodash-es/omit'
import * as Yup from 'yup'

import { EMPTY_LIST } from '../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../constants/validation'
import i18n from '../../../i18n'
import { getItemsIds } from '../../../utils/common'
import { formatDateToApi } from '../../../utils/date'
import { getCleanValues } from '../../../utils/formHelpers'
import { normalizeHtml } from '../../../utils/html'
import { parseServerValidation } from '../../../utils/validation'
import {
  ATTACH_ACTION_SELECT_ACTION_FILE,
  ATTACH_ACTION_SELECT_FILE_FROM_STORAGE,
} from '../../AttachActionSelect'
import { toBuildingAddressSelectItem } from '../../Building'
import { hasFormikFormEmptyRequired } from '../../Formik'
import { getPostTypeSelectOptions, POST_TYPE_TICKER } from '../../Post'
import { toAttachedRequestSelectItem } from '../../Request'
import {
  NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_DWELLERS,
  NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_LANDLORDS,
} from '../Newsboard.constants'

import {
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS_CHECKBOX,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS_CHECKBOX,
  NEWSBOARD_POST_FORM_RECIPIENTS_INITIAL_VALUES,
  NON_REGISTERED_USERS_OPTIONS,
  parseSubmitValues,
  REGISTERED_USERS_OPTIONS,
} from './FormFields'
import {
  ALL_BUILDING_GROUPS_COUNT,
  ALL_BUILDINGS_COUNT,
  NEWSBOARD_CREATION_FIELD_NAME_TEXT_TEMPLATE,
  NEWSBOARD_POST_FORM_FIELD_DWELLER_BUILDINGS,
  NEWSBOARD_POST_FORM_FIELD_NAME_COMMENTS,
  NEWSBOARD_POST_FORM_FIELD_NAME_END_DATE,
  NEWSBOARD_POST_FORM_FIELD_NAME_FILES,
  NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE,
  NEWSBOARD_POST_FORM_FIELD_NAME_POST_AGE,
  NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT,
  NEWSBOARD_POST_FORM_FIELD_NAME_START_DATE,
  NEWSBOARD_POST_FORM_FIELD_NAME_TITLE,
  NEWSBOARD_POST_FORM_FIELD_NAME_TYPE,
  NEWSBOARD_POST_FORM_FIELD_REQUESTS,
  NEWSBOARD_POST_FORM_RECIPIENTS_NAME_BUILDINGS,
  NEWSBOARD_POST_FORM_SERVER_ERROR_TRANSLATION_KEY,
  POST_AGE_MAX_VALUE,
} from './NewsboardPostForm.constants'

function getBuildingRecipientInitialValues(buildingOption) {
  return {
    ...NEWSBOARD_POST_FORM_RECIPIENTS_INITIAL_VALUES,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING]: [buildingOption],
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS]:
      NON_REGISTERED_USERS_OPTIONS[0],
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS_CHECKBOX]: true,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS]:
      REGISTERED_USERS_OPTIONS[0],
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS_CHECKBOX]: true,
  }
}

function getRecipientInitialValues(requestData) {
  if (!requestData?.building) {
    return NEWSBOARD_POST_FORM_RECIPIENTS_INITIAL_VALUES
  }

  return getBuildingRecipientInitialValues(
    toBuildingAddressSelectItem(requestData.building)
  )
}

export function getNewsboardCommentsSwitchLabelKey(isCommentsAllowed) {
  return isCommentsAllowed
    ? 'Newsboard.FormCommentsFieldSwitchOnLabelKey'
    : 'Newsboard.FormCommentsFieldSwitchOffLabelKey'
}

export function getBuildingPostFormInitialValues(buildingOption) {
  const recipientInitialValues =
    getBuildingRecipientInitialValues(buildingOption)

  return {
    getInitialValues(ukConfig, t) {
      return {
        ...getNewsboardPostFormInitialValues(ukConfig, t),
        [NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT]: parseSubmitValues(
          recipientInitialValues
        ),
      }
    },
    recipientInitialValues,
  }
}

export function getRequestPostFormInitialValues(requestData) {
  const recipientInitialValues = getRecipientInitialValues(requestData)

  return {
    getInitialValues(ukConfig, t) {
      const baseValues = getNewsboardPostFormInitialValues(ukConfig, t)

      return {
        ...baseValues,
        [NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE]:
          requestData.text || FORMIK_EMPTY_VALUE,
        [NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT]: parseSubmitValues(
          recipientInitialValues
        ),
        [NEWSBOARD_POST_FORM_FIELD_NAME_TITLE]:
          requestData.title || FORMIK_EMPTY_VALUE,
        [NEWSBOARD_POST_FORM_FIELD_NAME_TYPE]: getPostTypeSelectOptions(t)[0],
        [NEWSBOARD_POST_FORM_FIELD_REQUESTS]: [
          toAttachedRequestSelectItem(requestData),
        ],
      }
    },
    recipientInitialValues,
  }
}

export function getNewsboardPostFormValidationSchema(isStaffUserAcc, t) {
  return Yup.object({
    [NEWSBOARD_POST_FORM_FIELD_NAME_COMMENTS]: Yup.boolean(),
    [NEWSBOARD_POST_FORM_FIELD_NAME_END_DATE]: Yup.date()
      .nullable()
      .notRequired(),
    [NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE]: Yup.string()
      .trim()
      .required(true),
    [NEWSBOARD_POST_FORM_FIELD_NAME_POST_AGE]: Yup.number()
      .integer(t('Newsboard.PostAgeIncorrectValueKey'))
      .min(0, t('Newsboard.PostAgeIncorrectValueKey'))
      .max(
        POST_AGE_MAX_VALUE,
        t('Newsboard.PostAgeMaxIncorrectValueKey', { age: POST_AGE_MAX_VALUE })
      )
      .required(true),
    [NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT]: Yup.object()
      .required(true)
      .test({
        message: true,
        test: value => {
          if (value[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT]?.length > 0) {
            return true
          }

          // Non-staff picks only buildings; the audience is appended to the
          // payload from them on submit (see getNewsboardPostFormPayload).
          return (
            !isStaffUserAcc &&
            value[NEWSBOARD_POST_FORM_RECIPIENTS_NAME_BUILDINGS]?.length > 0
          )
        },
      }),
    [NEWSBOARD_POST_FORM_FIELD_NAME_START_DATE]: Yup.date()
      .nullable()
      .notRequired(),
    [NEWSBOARD_POST_FORM_FIELD_NAME_TITLE]: Yup.string().trim().required(true),
    [NEWSBOARD_POST_FORM_FIELD_NAME_TYPE]: Yup.object(),
    [NEWSBOARD_POST_FORM_FIELD_REQUESTS]: Yup.array(),
  })
}

export function getNewsboardPostFormInitialValues(ukConfig, t) {
  return {
    [NEWSBOARD_CREATION_FIELD_NAME_TEXT_TEMPLATE]: FORMIK_EMPTY_VALUE,
    [NEWSBOARD_POST_FORM_FIELD_NAME_COMMENTS]: true,
    [NEWSBOARD_POST_FORM_FIELD_NAME_END_DATE]: null,
    [NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE]: FORMIK_EMPTY_VALUE,
    [NEWSBOARD_POST_FORM_FIELD_NAME_POST_AGE]:
      ukConfig?.post_age_to_add_new_user ?? FORMIK_EMPTY_VALUE,
    [NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT]: FORMIK_EMPTY_VALUE,
    [NEWSBOARD_POST_FORM_FIELD_NAME_START_DATE]: null,
    [NEWSBOARD_POST_FORM_FIELD_NAME_TITLE]: FORMIK_EMPTY_VALUE,
    [NEWSBOARD_POST_FORM_FIELD_NAME_TYPE]: getPostTypeSelectOptions(t)[0],
    [NEWSBOARD_POST_FORM_FIELD_REQUESTS]: EMPTY_LIST,
  }
}

export function getNewsboardPostFormPayload({
  files,
  isAdminUserAcc,
  isStaffUserAcc,
  storageFiles,
  timezone,
  values,
}) {
  const payload = omit(
    {
      ...getCleanValues(values),
      ...values[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT],
    },
    [
      ALL_BUILDINGS_COUNT,
      ALL_BUILDING_GROUPS_COUNT,
      NEWSBOARD_POST_FORM_FIELD_DWELLER_BUILDINGS,
      NEWSBOARD_POST_FORM_FIELD_NAME_TYPE,
    ]
  )

  if (files.length > 0 || storageFiles.length > 0) {
    payload[NEWSBOARD_POST_FORM_FIELD_NAME_FILES] = getItemsIds([
      ...files,
      ...storageFiles,
    ])
  }

  if (payload[NEWSBOARD_POST_FORM_FIELD_REQUESTS]?.length > 0) {
    payload[NEWSBOARD_POST_FORM_FIELD_REQUESTS] = getItemsIds(
      payload[NEWSBOARD_POST_FORM_FIELD_REQUESTS]
    )
  }

  if (values[NEWSBOARD_POST_FORM_FIELD_NAME_START_DATE]) {
    payload[NEWSBOARD_POST_FORM_FIELD_NAME_START_DATE] = formatDateToApi(
      values[NEWSBOARD_POST_FORM_FIELD_NAME_START_DATE],
      timezone
    )
  }

  if (values[NEWSBOARD_POST_FORM_FIELD_NAME_END_DATE]) {
    payload[NEWSBOARD_POST_FORM_FIELD_NAME_END_DATE] = formatDateToApi(
      values[NEWSBOARD_POST_FORM_FIELD_NAME_END_DATE],
      timezone
    )
  }

  if (values[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT].user_info_status) {
    payload.user_info_status = values[
      NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT
    ].user_info_status.map(item => item.value)
  }

  if (
    !isAdminUserAcc &&
    values[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT].buildings
  ) {
    if (!isStaffUserAcc) {
      payload[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT] = [
        ...payload[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT],
        NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_LANDLORDS,
        NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_DWELLERS,
      ]
    }
  }

  if (!isStaffUserAcc) {
    payload[NEWSBOARD_POST_FORM_FIELD_NAME_COMMENTS] = false
  } else {
    // need to revert value cause in form field has reverse value named `Allow comments`
    payload[NEWSBOARD_POST_FORM_FIELD_NAME_COMMENTS] =
      !payload[NEWSBOARD_POST_FORM_FIELD_NAME_COMMENTS]
  }

  if (isAdminUserAcc) {
    if (
      values[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT][ALL_BUILDINGS_COUNT] ===
      values[NEWSBOARD_POST_FORM_FIELD_NAME_RECIPIENT][
        NEWSBOARD_POST_FORM_RECIPIENTS_NAME_BUILDINGS
      ]?.length
    ) {
      payload[NEWSBOARD_POST_FORM_RECIPIENTS_NAME_BUILDINGS] = EMPTY_LIST
    }
  }

  const message = payload[NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE]

  const isTickerType =
    values[NEWSBOARD_POST_FORM_FIELD_NAME_TYPE].value === POST_TYPE_TICKER

  if (message && !isTickerType) {
    // Normalize HTML only for a message created via Wysiwyg
    payload[NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE] = normalizeHtml(message)
  }

  return payload
}

export function getNewsboardPostFormAttachActions() {
  return [
    {
      label: i18n.t('Newsboard.AttachFileActionOptionKey'),
      value: ATTACH_ACTION_SELECT_ACTION_FILE,
    },
    {
      label: i18n.t('Newsboard.AttachFileFromStorageActionOptionKey'),
      value: ATTACH_ACTION_SELECT_FILE_FROM_STORAGE,
    },
  ]
}

export function checkNewsboardPostFormForEmptyRequired(errors) {
  return hasFormikFormEmptyRequired(
    omit(errors, [NEWSBOARD_POST_FORM_FIELD_NAME_END_DATE])
  )
}

export function parseNewsboardPostFormServerErrors(errors) {
  return mapValues(parseServerValidation(errors), value => {
    const translationKey =
      NEWSBOARD_POST_FORM_SERVER_ERROR_TRANSLATION_KEY[value]

    return translationKey ? i18n.t(translationKey) : value
  })
}
